import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMobileAuth } from "../contexts/mobile-auth-context";
import { analyzeAuthFlow } from "../lib/auth-flow";
import { useClientTranslations } from "../lib/i18n/client";
import { useGlobalLoading } from "./loading/global-loading-context";

interface SessionGuardProps {
	children: React.ReactNode;
	requireAuth?: boolean;
}

export function SessionGuard({
	children,
	requireAuth = true,
}: SessionGuardProps) {
	const { t } = useClientTranslations("common");
	const { isAuthenticated, isLoading, error, getAccessTokenSilently, user } =
		useAuth0();
	const { setLoading } = useGlobalLoading();
	const { appResumeCount } = useMobileAuth();
	const navigate = useNavigate();
	const location = useLocation();

	// Use ref to track if we've already validated
	const hasValidated = useRef(false);

	// Extract primitive values to prevent re-render loops
	const userSub = user?.sub;
	const pathname = location.pathname;

	// Reset validation flag when user changes (e.g., logout/login with different account)
	// NOTE: We intentionally do NOT reset on pathname change to avoid redirect loops
	// The Callback page now handles post-registration flow correctly by fetching status from API
	// biome-ignore lint/correctness/useExhaustiveDependencies: Only reset on user change, not navigation
	useEffect(() => {
		hasValidated.current = false;
	}, [userSub]);

	// Reset validation flag when app resumes from background
	// This ensures fresh auth checks after the app has been backgrounded
	// and prevents stale validation state after token refresh on resume
	useEffect(() => {
		if (appResumeCount > 0) {
			console.log("🔄 App resumed - resetting SessionGuard validation");
			hasValidated.current = false;
		}
	}, [appResumeCount]);

	// Track what changed
	const prevDeps = useRef({
		isAuthenticated,
		isLoading,
		error,
		requireAuth,
		navigate,
		getAccessTokenSilently,
		userSub,
		pathname,
		user,
	});

	// Sync Auth0 loading state with global loading
	useEffect(() => {
		setLoading(isLoading);
	}, [isLoading, setLoading]);

	// Handle authentication state changes
	useEffect(() => {
		const changes: string[] = [];
		if (prevDeps.current.isAuthenticated !== isAuthenticated)
			changes.push("isAuthenticated");
		if (prevDeps.current.isLoading !== isLoading) changes.push("isLoading");
		if (prevDeps.current.error !== error) changes.push("error");
		if (prevDeps.current.requireAuth !== requireAuth)
			changes.push("requireAuth");
		if (prevDeps.current.navigate !== navigate) changes.push("navigate");
		if (prevDeps.current.getAccessTokenSilently !== getAccessTokenSilently)
			changes.push("getAccessTokenSilently");
		if (prevDeps.current.userSub !== userSub) changes.push("userSub");
		if (prevDeps.current.pathname !== pathname) changes.push("pathname");
		if (prevDeps.current.user !== user) changes.push("user");

		// Track changes for debugging if needed

		prevDeps.current = {
			isAuthenticated,
			isLoading,
			error,
			requireAuth,
			navigate,
			getAccessTokenSilently,
			userSub,
			pathname,
			user,
		};

		// STOP THE LOOP: Only run once when Auth0 finishes loading
		if (hasValidated.current) {
			return;
		}

		if (!requireAuth) {
			return;
		}

		if (isLoading) {
			return;
		}

		if (error) {
			console.error("Auth0 error:", error);
			navigate("/login");
			hasValidated.current = true;
			return;
		}

		if (!isAuthenticated) {
			navigate("/login");
			hasValidated.current = true;
			return;
		}

		if (!user || !userSub) {
			return;
		}

		// Skip API check on callback, 2FA, and onboarding pages - use token claims
		// These pages are destination pages in the auth flow, so checking here would cause loops
		if (
			pathname === "/callback" ||
			pathname === "/2fa" ||
			pathname === "/onboarding" ||
			pathname.startsWith("/onboarding/")
		) {
			hasValidated.current = true;
			return;
		}

		// For returning users on other pages, fetch fresh status from API
		const checkStatusAndRedirect = async () => {
			try {
				// Try to get access token (may fail if refresh token not available yet)
				const token = await getAccessTokenSilently({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
					},
				});
				const statusResponse = await fetch(
					`${import.meta.env.VITE_API_URL}/api/user/status`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`,
							"Content-Type": "application/json",
						},
					},
				);

				if (statusResponse.ok) {
					const freshStatus = await statusResponse.json();

					// Update user object with fresh status
					const userWithFreshStatus = {
						...user,
						"https://eraborn.com/needs_2fa": freshStatus.needs_2fa,
						"https://eraborn.com/needs_onboarding":
							freshStatus.needs_onboarding,
						"https://eraborn.com/role": freshStatus.role,
						"https://eraborn.com/user_type": freshStatus.user_type,
						"https://eraborn.com/display_language":
							freshStatus.display_language,
					};

					// Analyze flow with fresh status
					const authFlowResult = analyzeAuthFlow(userWithFreshStatus, pathname);

					if (authFlowResult.shouldRedirect && authFlowResult.redirectPath) {
						navigate(authFlowResult.redirectPath, { replace: true });
					}
				} else {
					// Failed to fetch fresh status, fallback to token claims
				}

				hasValidated.current = true;
			} catch {
				// Don't block user on error, just use token claims
				hasValidated.current = true;
			}
		};

		checkStatusAndRedirect();
	}, [
		isAuthenticated,
		isLoading,
		error,
		userSub,
		user,
		requireAuth,
		navigate,
		getAccessTokenSilently,
		pathname,
	]);

	// Show loading while Auth0 is initializing
	if (isLoading) {
		return null; // Global loader will show
	}

	// Show error state if there's an Auth0 error
	if (error) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					flexDirection: "column",
					gap: "1rem",
				}}
			>
				<h2>{t("auth.error")}</h2>
				<p>{error.message}</p>
				<button
					type="button"
					onClick={() => navigate("/")}
					style={{
						padding: "8px 16px",
						backgroundColor: "#ef4444",
						color: "white",
						border: "none",
						borderRadius: "4px",
						cursor: "pointer",
					}}
				>
					{t("buttons.backToLogin")}
				</button>
			</div>
		);
	}

	// If authentication is required and user is not authenticated, don't render children
	if (requireAuth && !isAuthenticated) {
		return null;
	}

	// Render children if all checks pass
	return <>{children}</>;
}
